import React, { useState, useCallback } from "react";
import {
  CustomerPreorderMenu,
  PaymentRequiredType,
  WhoPaysType
} from "../../../../../../types/bookings";
import { EDSButton } from "../../../../../eds/EDSButton";
import { Input } from "../../../../../ui/input";
import { Label } from "../../../../../ui/label";
import { Switch } from "../../../../../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../../ui/select";

interface CustomerPreorderAdvancedOptionsProps {
  menu: CustomerPreorderMenu;
  menuIndex: number;
  onUpdate: (
    menuIndex: number,
    menuSettings: Partial<CustomerPreorderMenu>
  ) => Promise<void>;
  isSaving: boolean;
  numPeople?: number;
  currencySymbol?: string;
}

const CustomerPreorderAdvancedOptions: React.FC<
  CustomerPreorderAdvancedOptionsProps
> = ({
  menu,
  menuIndex,
  onUpdate,
  isSaving,
  numPeople,
  currencySymbol = "£"
}) => {
  const [localMenu, setLocalMenu] = useState<CustomerPreorderMenu>({ ...menu });
  const [hasChanges, setHasChanges] = useState(false);

  const updateLocalMenu = useCallback(
    (updates: Partial<CustomerPreorderMenu>) => {
      setLocalMenu((prev) => ({ ...prev, ...updates }));
      setHasChanges(true);
    },
    []
  );

  const handleSave = useCallback(async () => {
    await onUpdate(menuIndex, localMenu);
    setHasChanges(false);
  }, [menuIndex, localMenu, onUpdate]);

  const handleCancel = useCallback(() => {
    setLocalMenu({ ...menu });
    setHasChanges(false);
  }, [menu]);

  const handlePaymentTypeChange = useCallback(
    (value: string) => {
      const paymentType =
        value === "none" ? undefined : (value as PaymentRequiredType);
      const updates: Partial<CustomerPreorderMenu> = {
        payment_required: paymentType
      };

      if (paymentType !== "fixed_amount") {
        updates.payment_amount = undefined;
      }
      if (paymentType !== "percentage") {
        updates.payment_percentage = undefined;
      }

      updateLocalMenu(updates);
    },
    [updateLocalMenu]
  );

  const handleWhoPaysChange = useCallback(
    (value: string) => {
      updateLocalMenu({
        who_pays: value === "none" ? undefined : (value as WhoPaysType)
      });
    },
    [updateLocalMenu]
  );

  return (
    <div className="border-t border-border pt-3 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`payment-type-${menuIndex}`} className="text-sm">
            Payment Required
          </Label>
          <Select
            value={localMenu.payment_required ?? "none"}
            onValueChange={handlePaymentTypeChange}
            disabled={isSaving}
          >
            <SelectTrigger
              id={`payment-type-${menuIndex}`}
              className="w-full"
              aria-label="Payment required type"
            >
              <SelectValue placeholder="Select payment type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="full_amount">Full amount</SelectItem>
              <SelectItem value="fixed_amount">Fixed amount</SelectItem>
              <SelectItem value="percentage">Percentage of total</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {localMenu.payment_required === "fixed_amount" && (
          <div className="space-y-2">
            <Label htmlFor={`payment-amount-${menuIndex}`} className="text-sm">
              Fixed Amount
            </Label>
            <Input
              id={`payment-amount-${menuIndex}`}
              type="number"
              value={localMenu.payment_amount ?? ""}
              onChange={(e) =>
                updateLocalMenu({
                  payment_amount: parseFloat(e.target.value) ?? 0
                })
              }
              min="0"
              step="0.01"
              disabled={isSaving}
              aria-label="Fixed payment amount"
              autoComplete="off"
            />
          </div>
        )}

        {localMenu.payment_required === "percentage" && (
          <div className="space-y-2">
            <Label
              htmlFor={`payment-percentage-${menuIndex}`}
              className="text-sm"
            >
              Percentage
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id={`payment-percentage-${menuIndex}`}
                type="number"
                value={localMenu.payment_percentage ?? ""}
                onChange={(e) =>
                  updateLocalMenu({
                    payment_percentage: parseFloat(e.target.value) ?? 0
                  })
                }
                min="0"
                max="100"
                step="1"
                disabled={isSaving}
                aria-label="Payment percentage"
                autoComplete="off"
              />
              <span className="text-sm">%</span>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor={`who-pays-${menuIndex}`} className="text-sm">
            Who Pays
          </Label>
          <Select
            value={localMenu.who_pays ?? "none"}
            onValueChange={handleWhoPaysChange}
            disabled={isSaving}
          >
            <SelectTrigger
              id={`who-pays-${menuIndex}`}
              className="w-full"
              aria-label="Who pays for the order"
            >
              <SelectValue placeholder="Select who pays" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Not set</SelectItem>
              <SelectItem value="lead_booker">Lead booker</SelectItem>
              <SelectItem value="group_member">Group members</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor={`item-multiplier-${menuIndex}`} className="text-sm">
              Order in multiples of
            </Label>
            {numPeople && numPeople > 0 && (
              <button
                type="button"
                className="text-xs text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded"
                onClick={() => updateLocalMenu({ item_multiplier: numPeople })}
                disabled={isSaving}
                aria-label={`Set to the number of guests (${numPeople})`}
              >
                Set to number of guests ({numPeople})
              </button>
            )}
          </div>
          <Input
            id={`item-multiplier-${menuIndex}`}
            type="number"
            value={localMenu.item_multiplier ?? ""}
            onChange={(e) =>
              updateLocalMenu({
                item_multiplier: e.target.value
                  ? parseInt(e.target.value, 10)
                  : undefined
              })
            }
            min="1"
            step="1"
            placeholder="No limit"
            disabled={isSaving}
            aria-label="Order in multiples of"
            autoComplete="off"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`max-per-person-${menuIndex}`} className="text-sm">
            Max Items Per Person
          </Label>
          <Input
            id={`max-per-person-${menuIndex}`}
            type="number"
            value={localMenu.max_per_person ?? ""}
            onChange={(e) =>
              updateLocalMenu({
                max_per_person: e.target.value
                  ? parseInt(e.target.value, 10)
                  : undefined
              })
            }
            min="1"
            step="1"
            placeholder="No limit"
            disabled={isSaving}
            aria-label="Maximum items per person"
            autoComplete="off"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id={`one-menu-per-person-${menuIndex}`}
          checked={localMenu.one_menu_per_person ?? false}
          onCheckedChange={(checked) =>
            updateLocalMenu({ one_menu_per_person: checked })
          }
          disabled={isSaving}
          aria-label="Limit each guest to one complete menu"
        />
        <Label htmlFor={`one-menu-per-person-${menuIndex}`} className="text-sm">
          One menu per person
        </Label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`discount-amount-${menuIndex}`} className="text-sm">
            Discount (%)
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id={`discount-amount-${menuIndex}`}
              type="number"
              value={localMenu.discount_amount ?? ""}
              onChange={(e) =>
                updateLocalMenu({
                  discount_amount: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                  discount_type: e.target.value ? "percentage" : undefined
                })
              }
              min="0"
              max="100"
              step="1"
              placeholder="No discount"
              disabled={isSaving}
              aria-label="Discount percentage"
              autoComplete="off"
            />
            <span className="text-sm">%</span>
          </div>
        </div>

        {localMenu.who_pays === "lead_booker" && (
          <div className="flex items-center gap-2 pt-6">
            <Switch
              id={`hide-prices-${menuIndex}`}
              checked={localMenu.hide_prices ?? false}
              onCheckedChange={(checked) =>
                updateLocalMenu({ hide_prices: checked })
              }
              disabled={isSaving}
              aria-label="Hide prices from group members"
            />
            <Label htmlFor={`hide-prices-${menuIndex}`} className="text-sm">
              Hide prices from group
            </Label>
          </div>
        )}
      </div>

      <div className="border-t border-border pt-3">
        <h5 className="text-sm font-medium mb-3">Restrict Total Orders</h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor={`min-order-value-${menuIndex}`} className="text-sm">
              Minimum Order Value
            </Label>
            <div className="flex items-center gap-2">
              <span className="text-sm">{currencySymbol}</span>
              <Input
                id={`min-order-value-${menuIndex}`}
                type="number"
                value={localMenu.min_order_value ?? ""}
                onChange={(e) =>
                  updateLocalMenu({
                    min_order_value: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined
                  })
                }
                min="0"
                step="0.01"
                placeholder="No minimum"
                disabled={isSaving}
                aria-label="Minimum order value"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`max-order-value-${menuIndex}`} className="text-sm">
              Maximum Order Value
            </Label>
            <div className="flex items-center gap-2">
              <span className="text-sm">{currencySymbol}</span>
              <Input
                id={`max-order-value-${menuIndex}`}
                type="number"
                value={localMenu.max_order_value ?? ""}
                onChange={(e) =>
                  updateLocalMenu({
                    max_order_value: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined
                  })
                }
                min="0"
                step="0.01"
                placeholder="No maximum"
                disabled={isSaving}
                aria-label="Maximum order value"
                autoComplete="off"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-3">
        <h5 className="text-sm font-medium mb-3">Limit Pre-Orders</h5>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor={`limit-type-${menuIndex}`} className="text-sm">
              Limit Type
            </Label>
            <Select
              value={localMenu.limit_preorders?.type ?? "none"}
              onValueChange={(value) =>
                updateLocalMenu({
                  limit_preorders: {
                    ...localMenu.limit_preorders,
                    type:
                      value === "none"
                        ? undefined
                        : (value as "items" | "percentage")
                  }
                })
              }
              disabled={isSaving}
            >
              <SelectTrigger
                id={`limit-type-${menuIndex}`}
                className="w-full"
                aria-label="Pre-order limit type"
              >
                <SelectValue placeholder="No limit" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No limit</SelectItem>
                <SelectItem value="items">Number of items</SelectItem>
                <SelectItem value="percentage">Percentage of guests</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {localMenu.limit_preorders?.type && (
            <>
              <div className="space-y-2">
                <Label htmlFor={`limit-min-${menuIndex}`} className="text-sm">
                  Minimum
                </Label>
                <Input
                  id={`limit-min-${menuIndex}`}
                  type="number"
                  value={localMenu.limit_preorders?.min ?? ""}
                  onChange={(e) =>
                    updateLocalMenu({
                      limit_preorders: {
                        ...localMenu.limit_preorders,
                        min: e.target.value
                          ? parseInt(e.target.value, 10)
                          : undefined
                      }
                    })
                  }
                  min="0"
                  step="1"
                  placeholder="No minimum"
                  disabled={isSaving}
                  aria-label="Minimum pre-orders"
                  autoComplete="off"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`limit-max-${menuIndex}`} className="text-sm">
                  Maximum
                </Label>
                <Input
                  id={`limit-max-${menuIndex}`}
                  type="number"
                  value={localMenu.limit_preorders?.max ?? ""}
                  onChange={(e) =>
                    updateLocalMenu({
                      limit_preorders: {
                        ...localMenu.limit_preorders,
                        max: e.target.value
                          ? parseInt(e.target.value, 10)
                          : undefined
                      }
                    })
                  }
                  min="0"
                  step="1"
                  placeholder="No maximum"
                  disabled={isSaving}
                  aria-label="Maximum pre-orders"
                  autoComplete="off"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {hasChanges && (
        <div className="flex gap-2 pt-2">
          <EDSButton
            variant="default"
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </EDSButton>
          <EDSButton
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </EDSButton>
        </div>
      )}
    </div>
  );
};

export default CustomerPreorderAdvancedOptions;
